import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import styles from './Workspace.module.css';

const DEFAULT_WIDTH = 440;
const MIN_WIDTH = 300;
const MAX_WIDTH = 760;
const MOBILE_MAX = 899;

function readStoredWidth(storageKey: string, defaultWidth: number, min: number, max: number): number {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaultWidth;
    const n = Number(raw);
    if (!Number.isFinite(n)) return defaultWidth;
    return Math.min(max, Math.max(min, n));
  } catch {
    return defaultWidth;
  }
}

function useMediaMaxWidth(maxPx: number): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${maxPx}px)`).matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${maxPx}px)`);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [maxPx]);
  return matches;
}

/** detailFixed: lijst groeit, detail vaste breedte rechts (inbox). mainSidebar: smalle lijst links, detail vult rest (editors). */
export type ResizableWorkspaceLayout = 'detailFixed' | 'mainSidebar';

export type ResizableWorkspaceProps = {
  storageKey: string;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  layout?: ResizableWorkspaceLayout;
  detailOpen: boolean;
  main: ReactNode;
  detail: ReactNode | null;
  resizerAriaLabel?: string;
  onDetailDismiss?: () => void;
};

export function ResizableWorkspace({
  storageKey,
  defaultWidth = DEFAULT_WIDTH,
  minWidth = MIN_WIDTH,
  maxWidth = MAX_WIDTH,
  layout = 'detailFixed',
  detailOpen,
  main,
  detail,
  resizerAriaLabel = 'Breedte detailpaneel aanpassen',
  onDetailDismiss,
}: ResizableWorkspaceProps) {
  const isMobile = useMediaMaxWidth(MOBILE_MAX);
  const [panelWidth, setPanelWidth] = useState(() =>
    readStoredWidth(storageKey, defaultWidth, minWidth, maxWidth),
  );
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(panelWidth);

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!dragging.current) return;
      const delta =
        layout === 'mainSidebar' ? e.clientX - startX.current : startX.current - e.clientX;
      const next = Math.min(maxWidth, Math.max(minWidth, startWidth.current + delta));
      setPanelWidth(next);
    },
    [layout, minWidth, maxWidth],
  );

  const persistWidth = useCallback(
    (w: number) => {
      try {
        localStorage.setItem(storageKey, String(w));
      } catch {
        /* ignore */
      }
    },
    [storageKey],
  );

  const endDrag = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
    setPanelWidth((w) => {
      persistWidth(w);
      return w;
    });
  }, [onPointerMove, persistWidth]);

  useEffect(() => () => endDrag(), [endDrag]);

  useEffect(() => {
    if (!detailOpen || !onDetailDismiss) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onDetailDismiss();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detailOpen, onDetailDismiss]);

  function startDrag(e: React.PointerEvent) {
    if (e.button !== 0) return;
    dragging.current = true;
    startX.current = e.clientX;
    startWidth.current = panelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.currentTarget.setPointerCapture(e.pointerId);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
  }

  function resetWidth() {
    setPanelWidth(defaultWidth);
    persistWidth(defaultWidth);
  }

  if (!detailOpen) {
    return <div className={styles.workspaceSingle}>{main}</div>;
  }

  if (isMobile && detail) {
    return (
      <>
        <div className={styles.workspaceSingle}>{main}</div>
        <div
          className={styles.workspaceDetailOverlay}
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) onDetailDismiss?.();
          }}
        >
          <div className={styles.workspaceDetailOverlayInner}>{detail}</div>
        </div>
      </>
    );
  }

  const gridTemplateColumns =
    layout === 'mainSidebar'
      ? `${panelWidth}px 10px minmax(0, 1fr)`
      : `minmax(0, 1fr) 10px ${panelWidth}px`;

  return (
    <div
      className={styles.workspaceSplit}
      style={{ gridTemplateColumns }}
    >
      <div className={styles.workspaceMain}>{main}</div>
      <div
        className={styles.workspaceResizer}
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={panelWidth}
        aria-valuemin={minWidth}
        aria-valuemax={maxWidth}
        aria-label={resizerAriaLabel}
        onPointerDown={startDrag}
        onDoubleClick={resetWidth}
        title="Sleep om te vergroten/verkleinen · dubbelklik = standaard"
      />
      <div className={styles.workspaceDetail}>{detail}</div>
    </div>
  );
}
